class StickyVariantDrawer {
  constructor(root) {
    this.root = root;
    this.sectionId = root.dataset.sectionId;
    this.openCartDrawer = root.dataset.openCartDrawer === 'true';
    this.addingLabel = root.dataset.addingLabel || 'Adding...';
    this.successMessage = root.dataset.successMessage || 'Item added to your cart';
    this.errorMessage = root.dataset.errorMessage || 'Could not add to cart. Please try again.';

    this.triggerButton = root.querySelector('.sticky-variant-drawer__trigger');
    this.triggerText = root.querySelector('[data-svd-trigger-text]');
    this.spinner = this.triggerButton?.querySelector('.loading__spinner');
    this.feedbackEl = root.querySelector('[data-svd-feedback]');
    this.quantityInput = root.querySelector('.sticky-variant-drawer__quantity .quantity__input');
    this.quantityButtons = root.querySelectorAll('.sticky-variant-drawer__quantity .quantity__button');
    this.panel = root.querySelector('.sticky-variant-drawer__panel');
    this.variantItems = Array.from(root.querySelectorAll('.sticky-variant-drawer__variant-item'));

    this.cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    this.productFormButtons = document.querySelector('.product-form__buttons');
    this.isAdding = false;
    this.feedbackTimer = null;

    this.bindEvents();
  }

  bindEvents() {
    if (this.triggerButton) {
      this.triggerButton.addEventListener('click', () => {
        if (this.isAdding) return;
        if (this.panel.classList.contains('sticky-variant-drawer__panel--open')) {
          this.closePanel();
        } else {
          this.openPanel();
        }
      });
    }

    this.variantItems.forEach((item) => {
      item.addEventListener('click', () => this.onVariantClick(item));
    });

    this.quantityButtons.forEach((btn) => {
      btn.addEventListener('click', (event) => {
        event.preventDefault();
        this.adjustQuantity(btn.name === 'plus' ? 1 : -1);
      });
    });

    document.addEventListener('click', (event) => {
      if (!this.panel || this.panel.hidden) return;
      const target = event.target;
      if (this.root.contains(target)) return;
      this.closePanel();
    });

    this.initVisibilityObserver();
  }

  initVisibilityObserver() {
    if (!this.productFormButtons) {
      return;
    }

    this.hasScrolledPastOnce = false;

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const scrolledDown = window.scrollY > 0;

            if (!entry.isIntersecting && scrolledDown) {
              this.hasScrolledPastOnce = true;
            }

            if (this.hasScrolledPastOnce) {
              if (entry.isIntersecting) {
                this.root.classList.add('sticky-variant-drawer--hidden');
                this.root.classList.remove('sticky-variant-drawer--visible');
                this.closePanel();
              } else {
                this.root.classList.remove('sticky-variant-drawer--hidden');
                this.root.classList.add('sticky-variant-drawer--visible');
              }
            }
          });
        },
        {
          threshold: 0,
        }
      );

      observer.observe(this.productFormButtons);
    } else {
      const toggleOnScroll = () => {
        if (window.scrollY <= 0) return;
        const rect = this.productFormButtons.getBoundingClientRect();
        if (rect.bottom <= 0) {
          this.root.classList.remove('sticky-variant-drawer--hidden');
          this.root.classList.add('sticky-variant-drawer--visible');
        } else {
          this.root.classList.add('sticky-variant-drawer--hidden');
          this.root.classList.remove('sticky-variant-drawer--visible');
          this.closePanel();
        }
      };

      window.addEventListener('scroll', toggleOnScroll, { passive: true });
      toggleOnScroll();
    }
  }

  openPanel() {
    if (!this.panel || this.isAdding) return;
    this.panel.hidden = false;
    this.panel.classList.add('sticky-variant-drawer__panel--open');
    this.triggerButton?.setAttribute('aria-expanded', 'true');
  }

  closePanel() {
    if (!this.panel) return;
    this.panel.classList.remove('sticky-variant-drawer__panel--open');
    this.panel.hidden = true;
    this.triggerButton?.setAttribute('aria-expanded', 'false');
  }

  adjustQuantity(delta) {
    if (!this.quantityInput) return;
    const current = parseInt(this.quantityInput.value || '1', 10);
    const next = Math.max(1, current + delta);
    this.quantityInput.value = next;
  }

  setLoading(loading) {
    this.isAdding = loading;
    this.triggerButton?.classList.toggle('loading', loading);
    this.triggerButton?.setAttribute('aria-disabled', loading ? 'true' : 'false');
    this.spinner?.classList.toggle('hidden', !loading);

    if (this.triggerText) {
      const defaultLabel = this.triggerButton?.dataset.defaultLabel || 'Add to cart';
      this.triggerText.textContent = loading ? this.addingLabel : defaultLabel;
    }
  }

  hideFeedback() {
    if (!this.feedbackEl) return;
    this.feedbackEl.hidden = true;
    this.feedbackEl.textContent = '';
    this.feedbackEl.classList.remove(
      'sticky-variant-drawer__feedback--success',
      'sticky-variant-drawer__feedback--error'
    );
  }

  showFeedback(message, type = 'success') {
    if (!this.feedbackEl || !message) return;

    this.feedbackEl.textContent = message;
    this.feedbackEl.hidden = false;
    this.feedbackEl.classList.toggle('sticky-variant-drawer__feedback--success', type === 'success');
    this.feedbackEl.classList.toggle('sticky-variant-drawer__feedback--error', type === 'error');

    clearTimeout(this.feedbackTimer);
    this.feedbackTimer = setTimeout(() => this.hideFeedback(), 3000);
  }

  async updateCartSections(data) {
    if (!this.cart?.getSectionsToRender) return;

    if (data?.sections) {
      this.cart.renderContents(data);
      return;
    }

    const sections = this.cart.getSectionsToRender().map((section) => section.id).join(',');
    const response = await fetch(`${window.location.pathname}?sections=${sections}`);
    const sectionData = await response.json();

    this.cart.renderContents({ sections: sectionData });
  }

  async refreshCartIcon() {
    if (!this.cart?.getSectionsToRender) return;

    const cartIconSection = this.cart
      .getSectionsToRender()
      .find((section) => section.id === 'cart-icon-bubble');
    if (!cartIconSection) return;

    const response = await fetch(`${window.location.pathname}?section_id=cart-icon-bubble`);
    const html = await response.text();
    const target = document.getElementById(cartIconSection.id);
    if (!target) return;

    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const inner = parsed.querySelector('.shopify-section');
    if (inner) target.innerHTML = inner.innerHTML;
  }

  async onVariantClick(item) {
    const available = item.dataset.variantAvailable === 'true';
    if (!available || this.isAdding) return;

    const variantId = item.dataset.variantId;
    const quantity = this.quantityInput ? parseInt(this.quantityInput.value || '1', 10) : 1;
    if (!variantId) return;

    this.hideFeedback();
    this.setLoading(true);
    item.classList.add('loading');

    const body = {
      items: [
        {
          id: variantId,
          quantity,
        },
      ],
    };

    if (this.cart?.getSectionsToRender) {
      body.sections = this.cart.getSectionsToRender().map((section) => section.id).join(',');
      body.sections_url = window.location.pathname;
      this.cart.setActiveElement?.(item);
    }

    try {
      const response = await fetch(`${window.routes.cart_add_url}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (!response.ok || data.status) {
        const message = data.description || data.message || this.errorMessage;
        this.showFeedback(message, 'error');
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'sticky-variant-drawer',
            productVariantId: variantId,
            errors: message,
            message: data.message,
          });
        }
        return;
      }

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        await publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'sticky-variant-drawer',
          productVariantId: variantId,
          cartData: data,
        });
      }

      if (this.cart && this.openCartDrawer) {
        await this.updateCartSections(data);
      } else if (this.cart) {
        await this.refreshCartIcon();
      }

      this.showFeedback(this.successMessage, 'success');
      this.closePanel();
    } catch (error) {
      console.error('Error adding variant to cart', error);
      this.showFeedback(this.errorMessage, 'error');
    } finally {
      item.classList.remove('loading');
      this.setLoading(false);
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.sticky-variant-drawer').forEach((root) => new StickyVariantDrawer(root));
});
